import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import Typography from '@mui/material/Typography';
import SecondaryButton from '../common/SecondaryButton';
import { renderRich } from './richText';
import { LIGHT_GRAY } from '../../common/colors';

// One tier card for the /pricing billing grid. `highlighted` gives Team its
// "most organizations start here" treatment (thicker brand border + badge).
const PlanCard = ({ name, badge, priceMain, priceSub, tagline, featuresIntro, features, cta, highlighted }) => (
  <Box
    sx={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      p: 3,
      borderRadius: '12px',
      border: '1px solid',
      borderColor: highlighted ? 'primary.main' : LIGHT_GRAY,
      borderWidth: highlighted ? '2px' : '1px',
      backgroundColor: 'background.paper',
    }}>
    {/* One line, always: the badge hangs over the top edge and grows
        downward, so a second line covered the plan name (ocl_online#261). */}
    {badge && (
      <Box
        sx={{
          position: 'absolute',
          top: -14,
          left: 24,
          px: 1.5,
          py: 0.5,
          borderRadius: '999px',
          backgroundColor: 'primary.main',
          color: 'common.white',
          whiteSpace: 'nowrap',
        }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {badge}
        </Typography>
      </Box>
    )}
    <Typography variant="h5" component="h3" sx={{ color: 'surface.dark', mb: 1 }}>
      {name}
    </Typography>
    <Typography variant="h4" component="p" sx={{ color: 'surface.dark', fontWeight: 700, mb: priceSub ? 0.25 : 1 }}>
      {priceMain}
    </Typography>
    {priceSub && (
      <Typography variant="body2" sx={{ color: 'surface.contrastText', mb: 1 }}>
        {priceSub}
      </Typography>
    )}
    <Typography variant="body2" sx={{ color: 'surface.contrastText', mb: 2.5, minHeight: { md: 40 } }}>
      {tagline}
    </Typography>
    {featuresIntro && (
      <Typography variant="body2" sx={{ color: 'surface.dark', fontWeight: 600, mb: 1.5 }}>
        {featuresIntro}
      </Typography>
    )}
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, mb: 3, flexGrow: 1 }}>
      {features.map(feature => (
        <Box key={feature} component="li" sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.25 }}>
          <CheckIcon sx={{ fontSize: 18, color: 'primary.main', mt: '2px', flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: 'surface.contrastText' }}>
            {renderRich(feature)}
          </Typography>
        </Box>
      ))}
    </Box>
    {/* Smaller font + tighter padding than the button defaults: at the
        narrowest card widths (~265px, where the sm 2-up and the lg 4-up
        layouts start), the longest label — "Solicitar acceso anticipado" —
        wraps to two lines at default 16px/600 sizing, which looks cramped
        inside a fully-rounded pill button. A label may still wrap, which
        SecondaryButton doesn't allow: one too wide for its card would stick
        out of it, and from the last column past the page's edge
        (ocl_online#261). */}
    {/* cta.to is an in-app route (react-router, no page load); cta.href is
        anything else — the app's signup, a mailto. */}
    {highlighted ? (
      <Button {...(cta.to ? { component: RouterLink, to: cta.to } : { href: cta.href })} onClick={cta.onClick} variant="contained" disableElevation fullWidth sx={{ fontSize: '0.875rem', px: 1.5 }}>
        {cta.label}
      </Button>
    ) : (
      <SecondaryButton to={cta.to} href={cta.href} onClick={cta.onClick} fullWidth sx={{ fontSize: '0.875rem', px: 1.5, whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.3 }}>
        {cta.label}
      </SecondaryButton>
    )}
    {/* Lines under the button: the Pro terms, and why checkout didn't open. */}
    {cta.notes?.map(note => (
      <Typography key={note} variant="body2" sx={{ color: 'surface.contrastText', fontSize: '0.8125rem', mt: 1.5, textAlign: 'center' }}>
        {renderRich(note)}
      </Typography>
    ))}
  </Box>
);

export default PlanCard;
